import { ChiptaElement, html, css } from 'chipta-libs/main.js';
import { url, getQueryParam, updateQueryString } from 'chipta-libs/utils/url.js';
import { csrfHiddenInput } from 'chipta-libs/snippets/csrf-hidden-input.js';
import { userData } from 'chipta-libs/state/user-data.js';
import './language-switch.js';


class TicketShopMenuDropdownWidget extends ChiptaElement {

  static get properties() {
    return {
      menuOpen: {type: String}
    };
  }

  render() {

    return html`

      <outside-click @outside-click=${this.hideMenu}>

        <div id="menu-toggler" @click=${this.toggleMenu}>
          <font-awesome icon=${this.icon}></font-awesome>
        </div>

        <div id="menu" style="width: ${this.width}px;" ?visible=${this.menuOpen}>
          ${this.menuHeader}
          <ul>${this.menuItems}</ul>
        </div>

      </outside-click>

    `;

  }

  get width() {
    return 200;
  }

  get menuHeader() {
    return null;
  }

  static get styles() {

    return css`

      :host {
        display: block;
      }

      #menu-toggler {
        padding: 5px 10px;
        cursor: pointer;
        text-transform: uppercase;
      }

      #menu-header {
        display: flex;
        justify-content: center;
        align-items: center;
        margin: 15px;
        text-align: center;
        font-weight: 600;
        word-wrap: anywhere;
      }

      #menu-header font-awesome {
        margin-right: 10px;
      }

      #menu {
        display: none;
      }

      #menu[visible] {
        display: block;
        position: absolute;
        right: 0;
        top: 40px;
        padding: 0;
        margin: 0;
        background: white;
        box-shadow: 0 0 5px #999;
      }

      #menu[visible]:before,
      #menu[visible]:after {
        position: absolute;
        display: inline-block;
        content: '';
      }

      #menu[visible]:before {
        top: -16px;
        right: 8px;
        border: 9px solid transparent;
        border-bottom-color: #AAA;
      }

      #menu[visible]:after {
        top: -15px;
        right: 8px;
        border: 9px solid transparent;
        border-bottom-color: #FFF;
      }

      #menu ul {
        list-style: none;
        margin: 0;
        padding: 0;
      }

      #menu ul li .menu-item {
        display: block;
        padding: 7px;
        font-size: 14px;
        text-decoration: none;
        color: inherit;
        text-transform: capitalize;
      }

      #menu ul li button.menu-item {
        background: none;
        border: none;
        width: 100%;
        text-align: left;
        cursor: pointer;
      }

      #menu ul li .menu-item:hover {
        background: #D9D9D9;
      }

      #menu ul li font-awesome {
        display: inline-block;
        margin-right: 5px;
        width: 15px;
        text-align: center;
      }

      @media screen and (max-width: 500px) {

        #menu-toggler {
          padding: 10px 15px;
        }

        #menu[visible] {
          top: 40px;
        }

        #menu li a {
          padding: 13px;
        }

      }

    `;

  }

  hideMenu() {
    this.menuOpen = false;
  }

  toggleMenu() {
    this.menuOpen = !this.menuOpen;
  }

}

class AccountMenu extends TicketShopMenuDropdownWidget {

  constructor() {

    super();

    if (getQueryParam('user-logged')) {

      // When the user just logged in or out, then show the menu, so the user
      // sees that it indeed logged in/out.
      this.menuOpen = true;

      // Remove the `?user-logged=1` param, so the menu won't open every time
      // the page is reloaded.
      window.history.pushState(
        {}, document.title,
        (
          window.location.pathname
          + updateQueryString({ 'user-logged': null })
        )
      );

    }

  }

  get width() {
    if (userData.logged_in) {
      // Give more space for the `menuHeader` with the user's name and email
      // address.
      return 250;
    } else {
      return 200;
    }
  }

  get icon() {
    return 'user';
  }

  get menuHeader() {
    if (userData.logged_in) {
      return this.loggedInMenuHeader;
    } else {
      return this.loggedOutMenuHeader;
    }

  }

  get loggedOutMenuHeader() {
    return html`
      <div id="menu-header">
        <font-awesome icon="user"></font-awesome>
        <div>${gettext('not_logged_in')}</div>
      </div>
    `;
  }

  get loggedInMenuHeader() {

    return html`

      <div id="menu-header">

        <font-awesome icon="user"></font-awesome>

        <div>

          <div>
              ${userData.first_name}
              ${userData.last_name}
          </div>

          <div>${userData.email}</div>

        </div>

      </div>

    `;

  }

  get menuItems() {
    if (userData.logged_in) {
      return this.loggedInMenuItems;
    } else {
      return this.loggedOutMenuItems;
    }
  }

  get loggedInMenuItems() {

    const dashboardMenuItem = () => {

      if (jsVar('userData').is_organizer) {

        return html`
          <li>
            <a class="menu-item" href=${url('dashboard/')} target="_blank">
              <font-awesome icon="paintbrush"></font-awesome>
              ${gettext('organizer_dashboard')}
            </a>
          </li>
        `;

      }

    };

    return html`
      ${dashboardMenuItem()}
      <li>
        <a class="menu-item" href=${url('account/my-products/')}>
          <font-awesome icon="ticket_alt"></font-awesome>
          ${gettext('my_products')}
        </a>
      </li>
      <li>
        <a class="menu-item" href=${url('account/viewed-ticketshops/')}>
          <font-awesome icon="star"></font-awesome>
          ${gettext('viewed_ticketshops')}
        </a>
      </li>
      <li>
        <a class="menu-item" href=${url('account/personal-details/')}>
          <font-awesome icon="rectangle_list"></font-awesome>
          ${gettext('personal_details')}
        </a>
      </li>
      <li>
        <form action=${url('account/login/')} method="POST">
          ${csrfHiddenInput()}
          <input type="hidden" name="logout" value="1" />
          <input type="hidden" name="next" value=${window.location.pathname} />
          <button class="menu-item" type="submit">
            <font-awesome icon="right_from-bracket"></font-awesome>
            ${gettext('log_out')}
          </button>
        </form>
      </li>
    `;

  }

  get loggedOutMenuItems() {

    return html`

      <li>
        <a class="menu-item" href=${url('account/login/', { next: window.location.pathname })}>
          <font-awesome icon="right-to-bracket"></font-awesome>
          ${gettext('login')} / ${gettext('signup')}
        </a>
      </li>

    `;

  }

}

customElements.define('account-menu', AccountMenu);


class InfoMenu extends TicketShopMenuDropdownWidget {

  get icon() {
    return 'question';
  }

  get menuItems() {

    return html`

      <li>
        <a class="menu-item" href=${url('/')} target="_blank">
          <font-awesome icon="info"></font-awesome>
          ${gettext('about_chipta')}
        </a>
      </li>

      <li>
        <a class="menu-item" href=${url('faq')} target="_blank">
          <font-awesome icon="question"></font-awesome>
          ${gettext('ticket_faq')}
        </a>
      </li>

      <li>
        <a class="menu-item" href=${url('contact')} target="_blank">
          <font-awesome icon="envelope"></font-awesome>
          ${gettext('contact')}
        </a>
      </li>

    `;

  }

}

customElements.define('info-menu', InfoMenu);

class TicketshopMenu extends ChiptaElement {

  render() {

    return html`

      <ul id="items">

        <li class="item">
          <language-switch></language-switch>
        </li>

        <li class="item">
          <info-menu></info-menu>
        </li>

        <li class="item">
          <account-menu></account-menu>
        </li>

      </ul>

      <style>

        :host {
          display: block;
        }

        #items {
          display: flex;
          justify-content: flex-end;
          padding: 0;
          margin: 0;
          list-style: none;
        }

        .item {
          position: relative;
          background: white;
          font-size: 12px;
          color: ${_themeData.light_text_color};
          border: 1px solid #D9D9D9;
          border-width: 0 0 0 1px;
        }

        .item:first-child {
          border: none;
        }

      </style>

    `;

  }

}

customElements.define('ticketshop-menu', TicketshopMenu);
